import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ArrowRightIcon, FileTextIcon, ZapIcon, MapPinIcon, ShieldCheckIcon, LockIcon, CreditCardIcon } from 'lucide-react';
const benefits = ['Menos desgaste emocional', 'Más claridad mental', 'Mejores límites', 'Más tranquilidad', 'Más control emocional'];
export function HeroSection() {
  return <section className="relative w-full bg-hero overflow-hidden pt-10 md:pt-16 pb-12 px-4" data-id="element-162" data-mp="2wjz25a7z9d8b">
      <div className="max-w-4xl mx-auto text-center" data-id="element-163" data-mp="30latplaew8j5">
        {/* Kicker */}
        <motion.div initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="text-rose font-bold tracking-[0.18em] text-xs md:text-sm mb-7 uppercase" data-id="element-164" data-mp="30nevnxor2xgs">
          ⭐ Aula Mater ⭐
        </motion.div>

        {/* Title */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.1
      }} className="font-serif font-bold text-4xl sm:text-5xl md:text-6xl lg:text-[5rem] leading-[1.02] text-ink mb-5 uppercase tracking-tight" data-id="element-165" data-mp="3kb4irrlqs9jo">
          Cómo Trabajar con Gente Complicada
        </motion.h1>

        {/* Subtitle */}
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="text-ink-soft text-base md:text-lg mt-5 max-w-2xl mx-auto leading-relaxed" data-id="element-166" data-mp="2u4etch9lhx3b">
          Aprendé a manejar personas difíciles sin perder tu calma ni tu energía
          mental.
        </motion.p>

        {/* Italic rose accent */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6,
        delay: 0.35
      }} className="font-serif-italic text-rose text-lg md:text-2xl mt-5" data-id="element-167" data-mp="38turv71ykt1h">
          Claridad · Límites · Energía{' '}
          <span className="text-ink/40 mx-1" data-id="element-168" data-mp="3mufc87niuwx3">—</span> El método práctico
          completo
        </motion.div>

        {/* Mockup */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.4
      }} className="relative max-w-2xl mx-auto mt-10 md:mt-14" data-id="element-169" data-mp="lmffoll3n7f">
          <div className="absolute inset-8 bg-rose/15 blur-3xl rounded-full" data-id="element-170" data-mp="eevlpge8jslq" />
          <div className="absolute inset-8 bg-gold/10 blur-3xl rounded-full translate-x-10" data-id="element-171" data-mp="jdf97l8h9cg7" />
          <img src="https://cdn.magicpatterns.com/uploads/4Ra7yxLi7PGg3t7XN3AYBr/ChatGPT_Image_20_may_2026,_04_42_27_p.m..png" alt="Cómo Trabajar con Gente Complicada — Ebook" className="relative z-10 w-full h-auto rounded-2xl" data-id="element-172" data-mp="23uoel1lko1wh" />
        </motion.div>

        {/* Trust pills */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.55
      }} className="flex flex-wrap items-center justify-center gap-2.5 mt-10" data-id="element-173" data-mp="1pw7y4ixwqx15">
          {[{
          icon: FileTextIcon,
          text: '100% PDF'
        }, {
          icon: ZapIcon,
          text: 'Acceso en 5 min'
        }, {
          icon: MapPinIcon,
          text: 'Hecho en Argentina'
        }].map((p, i) => <div key={i} className="inline-flex items-center gap-2 bg-white border border-line text-ink text-sm font-medium px-4 py-2 rounded-full shadow-sm" data-id="element-174" data-mp="2l1w5cj0chv2p" data-mpi={i}>
              <p.icon className="w-4 h-4 text-rose" data-id="element-175" data-mp="2c3aocsaiuo41" />
              {p.text}
            </div>)}
        </motion.div>

        {/* Benefits */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.7
      }} className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-6 max-w-2xl mx-auto" data-id="element-176" data-mp="34qmyu43er5gz">
          {benefits.map((b, i) => <span key={i} className="inline-flex items-center gap-1.5 text-sm text-ink-soft" data-id="element-177" data-mp="2zyn3koya90qq" data-mpi={i}>
              <CheckIcon className="w-4 h-4 text-rose" strokeWidth={3} data-id="element-178" data-mp="3nno5rugivfnk" />
              {b}
            </span>)}
        </motion.div>

        {/* CTA */}
        <motion.a href="#oferta" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.8
      }} whileHover={{
        y: -2
      }} className="inline-flex items-center justify-center gap-2 bg-rose hover:bg-rose-deep text-white font-bold text-base md:text-lg px-12 py-5 rounded-full shadow-xl shadow-rose/30 mt-8 transition-all w-full sm:w-auto max-w-md" data-id="element-179" data-mp="5oc2xmj69mgi">
          <ArrowRightIcon className="w-5 h-5" data-id="element-180" data-mp="3sve6q3txo77j" />
          QUIERO ACCEDER AHORA
        </motion.a>

        {/* Trust row */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.9
      }} className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-5 text-xs md:text-sm text-muted" data-id="element-181" data-mp="3ntowurig5zl1">
          <span className="flex items-center gap-1.5" data-id="element-182" data-mp="1b831tojcd0rz">
            <ShieldCheckIcon className="w-4 h-4 text-rose" data-id="element-183" data-mp="1f6ag8cqc1q00" /> Garantía 7 días
          </span>
          <span className="text-rose/30" data-id="element-184" data-mp="1k7gdmnuxu1fv">|</span>
          <span className="flex items-center gap-1.5" data-id="element-185" data-mp="1k4ltyyjr0kni">
            <LockIcon className="w-4 h-4 text-rose" data-id="element-186" data-mp="1p46sx8yx4t08" /> Pago seguro
          </span>
          <span className="text-rose/30" data-id="element-187" data-mp="riiw4myjw8o6">|</span>
          <span className="flex items-center gap-1.5" data-id="element-188" data-mp="rfocgxnd2rvt">
            <CreditCardIcon className="w-4 h-4 text-rose" data-id="element-189" data-mp="whvtw2qyn05j" /> Mercado Pago
          </span>
        </motion.div>

        {/* Stats bar */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1,
        duration: 0.5
      }} className="mt-8 inline-flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 bg-white/70 backdrop-blur border border-line rounded-full px-6 py-3 text-sm font-medium text-ink" data-id="element-190" data-mp="11em96nuxxrpw">
          <span data-id="element-191" data-mp="18s8fu5ylgxa">
            <strong className="text-rose" data-id="element-192" data-mp="191whc7g1e0c">+2.000</strong> lectores
          </span>
          <span className="text-line" data-id="element-193" data-mp="3sd0fywaly6f5">•</span>
          <span data-id="element-194" data-mp="3sg4no3na7kak">
            <strong className="text-rose" data-id="element-195" data-mp="3neyts2hvxqzg">6</strong> módulos
          </span>
          <span className="text-line" data-id="element-196" data-mp="3nh1jelf4li3n">•</span>
          <span data-id="element-197" data-mp="3jdlp3vqj3tu4">
            <strong className="text-rose" data-id="element-198" data-mp="3je59dj3niubb">5</strong> bonus
          </span>
          <span className="text-line" data-id="element-199" data-mp="3em3nr6klfr8m">•</span>
          <span data-id="element-200" data-mp="3elc32qsjbdx4">
            ⭐ <strong className="text-rose" data-id="element-201" data-mp="3eksjl5n8khgz">4.8/5</strong>
          </span>
        </motion.div>
      </div>
    </section>;
}